/**
 * FDI Collections System - REST Client
 *
 * Encapsulates all HTTP calls to the collections REST API and normalizes
 * responses for frontend consumers.
 */

import CollectionService from '../../services/CollectionService.js';

const DEFAULT_VISIBILITY = 'all';
const DEFAULT_ORDER = 'desc';
const DEFAULT_ORDERBY = 'created_at';

const toBoolean = (value) => value === true || value === 1 || value === '1';

class CollectionAPI {
    constructor(service = null) {
        this.service = service || new CollectionService();
    }

    /**
     * Fetch paginated collections for the current user.
     */
    async listCollections(params = {}) {
        const {
            page = 1,
            per_page = 20,
            search = '',
            tag = '',
            visibility = DEFAULT_VISIBILITY,
            orderby = DEFAULT_ORDERBY,
            order = DEFAULT_ORDER,
            fdi_id = null,
        } = params;

        const response = await this.service.rest('/collections', {
            method: 'GET',
        }, {
            page,
            per_page,
            search,
            tag,
            visibility,
            orderby,
            order,
            fdi_id,
        });

        return this.#normalizeCollectionList(response, page, per_page);
    }

    /**
     * Create a new collection.
     */
    async createCollection(data) {
        const payload = await this.service.rest('/collections', {
            method: 'POST',
            body: JSON.stringify(data),
        });

        return this.#normalizeCollection(payload);
    }

    /**
     * Update an existing collection.
     */
    async updateCollection(id, data) {
        const payload = await this.service.rest(`/collections/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });

        return this.#normalizeCollection(payload);
    }

    /**
     * Delete a collection.
     */
    async deleteCollection(id) {
        await this.service.rest(`/collections/${id}`, {
            method: 'DELETE',
        });
        return { success: true };
    }

    /**
     * Bulk add FDI item to collections.
     */
    async addItems(collectionIds, fdiId) {
        return this.service.rest('/collections/bulk-save', {
            method: 'POST',
            body: JSON.stringify({
                collection_ids: collectionIds,
                fdi_id: fdiId,
            }),
        });
    }

    /**
     * Bulk remove FDI item from collections.
     */
    async removeItems(collectionIds, fdiId) {
        return this.service.rest('/collections/bulk-remove', {
            method: 'DELETE',
            body: JSON.stringify({
                collection_ids: collectionIds,
                fdi_id: fdiId,
            }),
        });
    }

    /**
     * Remove single item from a collection.
     */
    async removeItem(collectionId, fdiId) {
        await this.service.rest(`/collections/${collectionId}/items/${fdiId}`, {
            method: 'DELETE',
        });
        return { success: true };
    }

    /**
     * Remove multiple items from a collection.
     */
    async bulkRemoveItems(collectionId, fdiIds) {
        const tasks = Array.isArray(fdiIds) ? fdiIds : [];
        const results = await Promise.allSettled(
            tasks.map((fdiId) => this.removeItem(collectionId, fdiId)),
        );

        const hasFailures = results.some((result) => result.status === 'rejected');

        return {
            success: !hasFailures,
            results,
        };
    }

    /**
     * Sync collection items - Combined add/remove operation.
     */
    async syncItems(addIds, removeIds, fdiId) {
        return this.service.rest('/collections/sync', {
            method: 'POST',
            body: JSON.stringify({
                add_ids: addIds,
                remove_ids: removeIds,
                fdi_id: fdiId,
            }),
        });
    }

    /**
     * Search collections (lightweight payload for modals).
     */
    async searchCollections(params = {}) {
        const {
            search = '',
            tag = '',
            visibility = DEFAULT_VISIBILITY,
        } = params;

        const response = await this.service.rest('/collections/search', {
            method: 'GET',
        }, {
            search,
            tag,
            visibility,
        });

        return this.#normalizeSearchResults(response);
    }

    /**
     * Get available user tags.
     */
    async getTags() {
        const payload = await this.service.rest('/collections/tags', {
            method: 'GET',
        });

        const tagsArray = Array.isArray(payload?.tags)
            ? payload.tags
            : Array.isArray(payload)
                ? payload
                : [];

        const normalised = tagsArray.map((tag) => {
            if (typeof tag === 'string') {
                return { name: tag, count: 0 };
            }

            if (tag && typeof tag === 'object') {
                return {
                    name: String(tag.name || ''),
                    count: Number(tag.count || 0),
                };
            }

            return { name: '', count: 0 };
        }).filter((tag) => tag.name !== '');

        const meta = payload?.meta && typeof payload.meta === 'object'
            ? payload.meta
            : { total: normalised.length };

        return { tags: normalised, meta };
    }

    /**
     * Fetch collection settings (limits etc.).
     */
    async getSettings() {
        return this.service.rest('/admin/collection-settings', {
            method: 'GET',
        });
    }

    /**
     * Duplicate a collection (always returns new collection data).
     */
    async duplicateCollection(id) {
        const payload = await this.service.rest(`/collections/${id}/duplicate`, {
            method: 'POST',
        });

        return this.#normalizeCollection(payload);
    }

    /**
     * Toggle collection visibility.
     */
    async toggleVisibility(id, isPublic) {
        const payload = await this.service.rest(`/collections/${id}/visibility`, {
            method: 'PUT',
            body: JSON.stringify({
                is_public: isPublic,
            }),
        });

        return this.#normalizeCollection(payload);
    }

    /**
     * Fetch items inside a collection.
     */
    async getCollectionItems(id, params = {}) {
        const {
            page = 1,
            per_page = 50,
        } = params;

        const response = await this.service.rest(`/collections/${id}/items`, {
            method: 'GET',
        }, {
            page,
            per_page,
        });

        const items = Array.isArray(response?.items) ? response.items : [];
        return {
            items,
            total: Number(response?.total) || items.length,
            pages: Number(response?.pages) || 1,
            currentPage: Number(response?.current_page) || page,
        };
    }

    /**
     * Get basic FDI item data for modal display.
     */
    async getFdiItem(fdiId) {
        return this.service.rest(`/fdi/${fdiId}`, {
            method: 'GET',
        });
    }

    /**
     * Cancel in-flight HTTP requests.
     */
    cancelAll() {
        if (typeof this.service.cancelAll === 'function') {
            this.service.cancelAll();
        }
    }

    /**
     * Inspect request stats (useful for debugging).
     */
    getStats() {
        if (typeof this.service.getStats === 'function') {
            return this.service.getStats();
        }
        return { activeRequests: 0, queuedRequests: 0 };
    }

    /**
     * Normalize list response.
     */
    #normalizeCollectionList(payload, fallbackPage, fallbackPerPage) {
        const collections = Array.isArray(payload?.collections) ? payload.collections : [];
        const total = Number(payload?.total) || collections.length;
        const pages = Number(payload?.pages) || Math.max(1, Math.ceil(total / fallbackPerPage));
        const currentPage = Number(payload?.current_page) || fallbackPage;

        const normalizedCollections = collections.map((collection) => this.#normalizeCollection(collection));

        return {
            collections: normalizedCollections,
            pagination: {
                total,
                pages,
                currentPage,
                perPage: fallbackPerPage,
            },
        };
    }

    /**
     * Normalize single collection shape.
     */
    #normalizeCollection(collection) {
        if (!collection || typeof collection !== 'object') {
            return {};
        }

        return {
            id: Number(collection.id),
            name: collection.name || '',
            description: collection.description || '',
            tags: Array.isArray(collection.tags) ? collection.tags : [],
            is_public: toBoolean(collection.is_public),
            item_count: Number(collection.item_count) || 0,
            has_fdi: toBoolean(collection.has_fdi),
            view_count: Number(collection.view_count) || 0,
            created_at: collection.created_at || null,
            updated_at: collection.updated_at || null,
        };
    }

    /**
     * Normalize search response payload.
     */
    #normalizeSearchResults(payload) {
        if (!payload || typeof payload !== 'object') {
            return {
                collections: [],
                total: 0,
            };
        }

        const collections = Array.isArray(payload.collections) ? payload.collections : [];
        const normalized = collections.map((collection) => this.#normalizeCollection(collection));

        return {
            collections: normalized,
            total: Number(payload.total) || normalized.length,
        };
    }
}

export { CollectionAPI };
export default CollectionAPI;
